import { useCallback, useMemo, useState } from "react";
import { TransactionFiltersDto } from "@common/dto/transactions/getTransactions/transaction-filters.dto";
import { TransactionView } from "@common/types/views/transactionViews";
import { pluralize } from "@common/helpers/string";
import { useClasses } from "@api/class.api";
import { Transaction } from "@common/types/transaction";
import { useIsAccrualLedgerEnabled } from "@hooks/useIsAccrualLedgerEnabled";
import { TransactionViewDrawer } from "@components/transactions/TransactionViewDrawer/TransactionViewDrawer";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { type ActiveFilter, FilterTabs } from "../../pageHeader/filters/lib";
import { useOverlays } from "../../../contexts/OverlayContext";
import { type PaginatedTransactionsActionsContextValue } from "../../transactions/PaginatedTransactionsTable.context";
import { BulkUpdateParams } from "../../transactions/TransactionsBulkActions/lib";
import { ActionsCard } from "./ActionsCard/ActionsCard";
import { StaticConditionsRow } from "./ActionsCard/StaticConditionsRow";
import { SimilarTransactionsNewValueContext } from "./SimilarTransactionsNewValueContext";
import { useSimilarTransactionsViewConfig } from "./useSimilarTransactionsViewConfig";
import {
    getTransactionColumnForUpdate,
    getPropertyLabel,
    NEW_VALUE_COLUMN_ID,
} from "./SimilarTransactionsUpdateDrawer.lib";
import {
    isEntityValue,
    toSourceSplitParams,
} from "./ActionsCard/seedActionRow";
import { SimilarTransactionsEntityFilter } from "./SimilarTransactionsEntityFilter";
import styles from "./SimilarTransactionsUpdateDrawer.module.scss";

const PRIORITIZED_ACTIVE_FILTERS: ActiveFilter[] = ["direction"];
const DISABLED_ACTIVE_FILTERS: ActiveFilter[] = ["direction"];

function isStringArray(value: BulkUpdateParams["value"]): value is string[] {
    return (
        Array.isArray(value) && value.every((item) => typeof item === "string")
    );
}

function getValueToAssignWithValue({
    current,
    value,
    label,
}: {
    current: BulkUpdateParams;
    value: BulkUpdateParams["value"];
    label?: string;
}): BulkUpdateParams {
    switch (current.update) {
        case "categoryId":
            return typeof value === "string"
                ? { update: "categoryId", value }
                : current;
        case "memo":
            return typeof value === "string"
                ? { update: "memo", value }
                : current;
        case "taxQuestionAnswers":
            return current;
        case "entity":
            return isEntityValue(value) ? { update: "entity", value } : current;
        case "classes":
            return isStringArray(value)
                ? { update: "classes", value }
                : current;
        case "counterpartyId":
            if (typeof value !== "string" && value !== null) {
                return current;
            }
            return label === undefined
                ? { update: "counterpartyId", value }
                : { update: "counterpartyId", value, counterpartyName: label };
        case "customerId":
            if (typeof value !== "string" && value !== null) {
                return current;
            }
            return label === undefined
                ? { update: "customerId", value }
                : { update: "customerId", value, customerName: label };
        case "pairedTransferId":
            return typeof value === "string" || value === null
                ? { update: "pairedTransferId", value }
                : current;
        case "coa":
            if (typeof value !== "string" && value !== null) {
                return current;
            }
            return label === undefined
                ? { update: "coa", value }
                : { update: "coa", value, accountName: label };
        case "accrualCoa":
            if (typeof value !== "string" && value !== null) {
                return current;
            }
            return label === undefined
                ? { update: "accrualCoa", value }
                : { update: "accrualCoa", value, accountName: label };
        case "excludeFrom1099":
            return typeof value === "boolean"
                ? { update: "excludeFrom1099", value }
                : current;
    }
}

export const SimilarTransactionsUpdateDrawerNext: React.FC = () => {
    const { getOverlayInfo, closeOverlay, overlayIsClosing } = useOverlays();
    const overlayInfo = getOverlayInfo("similarTransactionsUpdate");

    if (!overlayInfo?.data) {
        return null;
    }

    return (
        <SimilarTransactionsUpdateDrawerNextContent
            data={overlayInfo.data}
            isClosing={overlayIsClosing}
            onClose={closeOverlay}
        />
    );
};

const SimilarTransactionsUpdateDrawerNextContent: React.FC<{
    data: {
        filters: TransactionFiltersDto;
        valueToAssign: BulkUpdateParams;
        displayedChangedEntityName?: string;
        view: TransactionView;
        totalTransactions: number;
        updatedTransaction: Transaction;
    };
    isClosing: boolean;
    onClose: () => void;
}> = ({ data, isClosing, onClose }) => {
    const {
        filters,
        valueToAssign: initialValueToAssign,
        displayedChangedEntityName: initialDisplayedChangedEntityName,
        view,
        totalTransactions,
        updatedTransaction,
    } = data;

    const [valueToAssign, setValueToAssign] = useState(initialValueToAssign);
    const [displayedChangedEntityName, setDisplayedChangedEntityName] =
        useState(initialDisplayedChangedEntityName);

    // D2: seeds the split key on drawer-created SIMILAR_TO rules. Safe to take
    // from the single representative transaction — the bulk flow only reaches
    // this drawer when the preview guard has already verified every source row
    // shares one effective split key.
    const sourceSplitParams = useMemo(
        () => toSourceSplitParams(updatedTransaction),
        [updatedTransaction],
    );

    const propertyLabel = getPropertyLabel(valueToAssign.update);
    const similarToFallbackLabel =
        updatedTransaction.displayDescription ??
        updatedTransaction.bankDescription ??
        updatedTransaction.normalizedBankDescription ??
        undefined;
    const viewConfig = useSimilarTransactionsViewConfig(valueToAssign);
    const { isActivated: classesEnabled } = useClasses();
    const isAccrualLedgerFilterEnabled = useIsAccrualLedgerEnabled();

    const [newValuesMap, setNewValuesMap] = useState<
        Record<number, BulkUpdateParams["value"]>
    >({});
    const [newValueLabelsMap, setNewValueLabelsMap] = useState<
        Record<number, string>
    >({});

    const setNewValue = useCallback(
        (
            transactionId: number,
            value: BulkUpdateParams["value"],
            label?: string,
        ) => {
            setNewValuesMap((prev) => ({ ...prev, [transactionId]: value }));
            setNewValueLabelsMap((prev) => {
                if (label === undefined) {
                    const next = { ...prev };
                    delete next[transactionId];
                    return next;
                }
                return { ...prev, [transactionId]: label };
            });
        },
        [],
    );

    const setNewValues = useCallback(
        (
            updates: Array<{
                transactionId: number;
                value: BulkUpdateParams["value"];
                label?: string;
            }>,
        ) => {
            setNewValuesMap((prev) => {
                const next = { ...prev };
                for (const update of updates) {
                    next[update.transactionId] = update.value;
                }
                return next;
            });
            setNewValueLabelsMap((prev) => {
                const next = { ...prev };
                for (const update of updates) {
                    if (update.label === undefined) {
                        delete next[update.transactionId];
                    } else {
                        next[update.transactionId] = update.label;
                    }
                }
                return next;
            });
        },
        [],
    );

    const setBaseValue = useCallback(
        (value: BulkUpdateParams["value"], label?: string) => {
            setValueToAssign((current) =>
                getValueToAssignWithValue({ current, value, label }),
            );
            setDisplayedChangedEntityName(label);
            setNewValuesMap({});
            setNewValueLabelsMap({});
        },
        [],
    );

    const contextValue = useMemo(
        () => ({
            newValuesMap,
            newValueLabelsMap,
            setNewValue,
            setNewValues,
            setBaseValue,
            valueToAssign,
            displayedChangedEntityName,
        }),
        [
            newValuesMap,
            newValueLabelsMap,
            setNewValue,
            setNewValues,
            setBaseValue,
            valueToAssign,
            displayedChangedEntityName,
        ],
    );

    const customView = useMemo(() => {
        const updateColumn = getTransactionColumnForUpdate(
            valueToAssign.update,
        );
        const updateColumnId = updateColumn?.id;
        const pinnedUpdateColumn:
            | TransactionView["columns"][number]
            | undefined = updateColumn
            ? {
                  ...updateColumn,
                  options: { ...(updateColumn.options ?? {}), pin: "right" },
              }
            : undefined;
        const newValueColumn: TransactionView["columns"][number] = {
            id: NEW_VALUE_COLUMN_ID,
            options: { pin: "right" },
        };
        const rightPinnedUpdateColumns: TransactionView["columns"] =
            pinnedUpdateColumn
                ? [pinnedUpdateColumn, newValueColumn]
                : [newValueColumn];

        const newColumns: TransactionView["columns"] = [
            ...view.columns.filter(
                (column) =>
                    column.id !== updateColumnId &&
                    column.id !== NEW_VALUE_COLUMN_ID,
            ),
            ...rightPinnedUpdateColumns,
        ];

        return {
            ...view,
            columns: newColumns,
            options: { ...view.options, readonly: true },
        };
    }, [view, valueToAssign.update]);

    const availableFilters = useMemo(
        () => [
            FilterTabs.DATE,
            FilterTabs.CATEGORY,
            FilterTabs.ACCOUNT,
            FilterTabs.AMOUNT,
            ...(classesEnabled ? [FilterTabs.CLASS] : []),
            FilterTabs.RULES,
            FilterTabs.COUNTERPARTY,
            FilterTabs.NOTE,
            FilterTabs.RECEIPTS,
            FilterTabs.SPLIT,
            FilterTabs.RECONCILIATION,
            FilterTabs.MATCHED,
            FilterTabs.LEDGER,
            ...(isAccrualLedgerFilterEnabled
                ? [FilterTabs.ACCRUAL_LEDGER]
                : []),
            FilterTabs.TRANSACTION_LOCKED,
            FilterTabs.STATUS,
        ],
        [classesEnabled, isAccrualLedgerFilterEnabled],
    );

    const headerAddon = useMemo(
        () => (
            <div className={styles.header}>
                Confirm {propertyLabel} and rule for {totalTransactions} similar{" "}
                {pluralize("transaction", totalTransactions)}
            </div>
        ),
        [propertyLabel, totalTransactions],
    );

    const staticFilterPill = useMemo(
        () => (
            <StaticConditionsRow
                filters={filters}
                similarToFallbackLabel={similarToFallbackLabel}
            />
        ),
        [filters, similarToFallbackLabel],
    );

    const filterRowActions = useCallback(
        (options: {
            currentView: TransactionView;
            onViewChange: PaginatedTransactionsActionsContextValue["setCurrentView"];
        }) => (
            <>
                <SimilarTransactionsEntityFilter filters={filters} />
                <ColumnSettings<TransactionView>
                    view={options.currentView}
                    defaultView={customView}
                    viewConfig={viewConfig}
                    onViewChange={options.onViewChange}
                />
            </>
        ),
        [customView, filters, viewConfig],
    );

    const footerAddon = useCallback(
        () => (
            <ActionsCard
                valueToAssign={valueToAssign}
                displayedChangedEntityName={displayedChangedEntityName}
                filters={filters}
                sourceSplitParams={sourceSplitParams}
                newValuesMap={newValuesMap}
                totalTransactions={totalTransactions}
                similarToFallbackLabel={similarToFallbackLabel}
                onDone={onClose}
            />
        ),
        [
            valueToAssign,
            displayedChangedEntityName,
            filters,
            sourceSplitParams,
            newValuesMap,
            totalTransactions,
            similarToFallbackLabel,
            onClose,
        ],
    );

    return (
        <SimilarTransactionsNewValueContext.Provider value={contextValue}>
            <TransactionViewDrawer
                view={customView}
                open={!isClosing}
                onClose={onClose}
                parentFilters={filters}
                headerAddon={headerAddon}
                viewConfigOverride={viewConfig}
                contentClassName={styles.drawerContent}
                staticFilterPill={staticFilterPill}
                availableFilters={availableFilters}
                directionSwitchReadonly
                prioritizedActiveFilters={PRIORITIZED_ACTIVE_FILTERS}
                disabledActiveFilters={DISABLED_ACTIVE_FILTERS}
                filterRowActions={filterRowActions}
                footerAddon={footerAddon}
            />
        </SimilarTransactionsNewValueContext.Provider>
    );
};
